import React, {useEffect} from 'react';
import {Link} from 'react-router-dom';
import {useAuth} from '../auth/AuthContext';

const maskEmail = (email) => (email ? `${email.slice(0, 5)}...@...` : null);

const usernameFromToken = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace('-', '+').replace('_', '/');
        const payload = JSON.parse(window.atob(base64));
        return payload.username || payload.email || null;
    } catch (error) {
        return null;
    }
};

function Sidebar() {
    const {auth, isAuthenticated, isSuperAdmin} = useAuth();
    const username = isAuthenticated && auth.token ? usernameFromToken(auth.token) : null;

    // Treeview (submeniurile pliabile) trebuie initializat DUPA montarea sidebar-ului.
    // adminlte.js se leaga de [data-widget="treeview"] la document ready, moment in care
    // meniul React inca nu exista - in dev nu se observa (StrictMode monteaza de doua ori,
    // a doua montare nimereste dupa init), dar in build-ul de producție submeniurile
    // rămân inerte. Reinitializam si la schimbarea rolului, fiindca "Rapoarte" apare
    // abia atunci in DOM.
    useEffect(() => {
        const $ = window.jQuery;

        if (!$ || typeof $.fn?.Treeview !== 'function') {
            return;
        }

        $('[data-widget="treeview"]').each(function () {
            // 'init' e idempotent in AdminLTE 3: re-legarea nu dubleaza handlerele
            $(this).Treeview('init');
        });
    }, [isAuthenticated, isSuperAdmin]);

    // Click pe content (in afara ambelor sidebar-uri) -> inchide sidebar-ul deschis (mai ales pe mobil).
    // Reutilizeaza butoanele reale de toggle din Navbar (simuleaza click), nu reimplementam
    // logica interna a AdminLTE (evita desincronizarea cu starea reala a plugin-ului jQuery).
    useEffect(() => {
        const onDocumentClick = (e) => {
            // click-ul pe chiar butoanele de toggle e gestionat deja de AdminLTE -
            // altfel il inchidem la loc, in acelasi click in care s-a deschis
            if (e.target.closest('[data-widget="pushmenu"]') || e.target.closest('[data-widget="control-sidebar"]')) {
                return;
            }

            const insideLeft = e.target.closest('.main-sidebar');
            const insideRight = e.target.closest('.control-sidebar');

            if (!insideLeft && document.body.classList.contains('sidebar-open')) {
                document.querySelector('[data-widget="pushmenu"]')?.click();
            }
            if (!insideRight && document.body.classList.contains('control-sidebar-slide-open')) {
                document.querySelector('[data-widget="control-sidebar"]')?.click();
            }
        };
        document.addEventListener('click', onDocumentClick);
        return () => document.removeEventListener('click', onDocumentClick);
    }, []);

    // Click pe un link care navigheaza din sidebar-ul stang (meniu sau logo/brand)
    // -> inchide sidebar-ul (navigare + close pe mobil).
    // Exceptii: click pe spatiu gol si click pe parintele unui submeniu (nav-treeview), care
    // doar pliaza/depliaza - altfel submeniul s-ar deschide intr-un sidebar tocmai inchis.
    const closeLeftSidebarIfOpen = (e) => {
        const link = e.target.closest('a.nav-link, a.brand-link');
        if (!link) {
            return;
        }
        if (link.nextElementSibling?.classList.contains('nav-treeview')) {
            return;
        }
        if (document.body.classList.contains('sidebar-open')) {
            document.querySelector('[data-widget="pushmenu"]')?.click();
        }
    };

    return (
        <div>

            <aside className="main-sidebar sidebar-dark-primary elevation-4" onClick={closeLeftSidebarIfOpen}>
                {/* Brand Logo */}
                <Link to="/" className="brand-link">
                    <img src="/logo.png" alt="App.WebNou.Ro Logo" className="brand-image img-circle elevation-3"
                         style={{opacity: .8}}/>
                    <span className="brand-text font-weight-light">App.WebNou.Ro</span>
                </Link>

                {/* Sidebar */}
                <div className="sidebar">
                    {/* Sidebar user panel - dinamic in functie de autentificare */}
                    <div className="user-panel mt-3 pb-3 mb-3 d-flex">
                        <div className="image">
                            <img src="/dist/img/avatar.png" className="img-circle elevation-2" alt="UserImg"/>
                        </div>
                        <div className="info">
                            {isAuthenticated ? (
                                <span className="d-block">{maskEmail(username) || 'Cont conectat'}</span>
                            ) : (
                                <Link to="/login" className="d-block">Login</Link>
                            )}
                        </div>
                    </div>

                    {/* SidebarSearch Form */}
                    <div className="form-inline">
                        <div className="input-group" data-widget="sidebar-search">
                            <input className="form-control form-control-sidebar" type="search" placeholder="Search"
                                   aria-label="Search"/>
                            <div className="input-group-append">
                                <button className="btn btn-sidebar">
                                    <i className="fas fa-search fa-fw"></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Sidebar Menu - aceleasi rute ca in Navbar */}
                    <nav className="mt-2">
                        <ul
                            className="nav nav-pills nav-sidebar flex-column"
                            data-widget="treeview"
                            role="menu"
                            data-accordion="false"
                        >
                            {isAuthenticated ? (
                                <>
                                    <li className="nav-item">
                                        <Link to="/" className="nav-link">
                                            <i className="nav-icon fas fa-home"></i>
                                            <p>Home</p>
                                        </Link>
                                    </li>

                                    {/* Admin: submeniu pliabil (nav-treeview AdminLTE). */}
                                    <li className="nav-item">
                                        <a href="#" className="nav-link" onClick={(e) => e.preventDefault()}>
                                            <i className="nav-icon fas fa-cog"></i>
                                            <p>
                                                Admin
                                                <i className="right fas fa-angle-left"></i>
                                            </p>
                                        </a>
                                        <ul className="nav nav-treeview">
                                            <li className="nav-item">
                                                <Link to="/admin" className="nav-link">
                                                    <i className="far fa-images nav-icon"></i>
                                                    <p>Companie</p>
                                                </Link>
                                            </li>
                                            <li className="nav-item">
                                                <Link to="/logo" className="nav-link">
                                                    <i className="far fa-image nav-icon"></i>
                                                    <p>Logo</p>
                                                </Link>
                                            </li>
                                            <li className="nav-item">
                                                <Link to="/generate-apk" className="nav-link">
                                                    <i className="fas fa-mobile-alt nav-icon"></i>
                                                    <p>Generare APK</p>
                                                </Link>
                                            </li>
                                        </ul>
                                    </li>

                                    <li className="nav-item">
                                        <Link to="/contact" className="nav-link">
                                            <i className="nav-icon fas fa-envelope"></i>
                                            <p>Contact</p>
                                        </Link>
                                    </li>

                                    {/* Rapoarte: doar super-admin. Submeniu AdminLTE
                                        (nav-treeview) - clasele plugin-ului, nu logica
                                        proprie de deschidere/inchidere. */}
                                    {isSuperAdmin && (
                                        <li className="nav-item">
                                            <a href="#" className="nav-link" onClick={(e) => e.preventDefault()}>
                                                <i className="nav-icon fas fa-chart-bar"></i>
                                                <p>
                                                    Rapoarte
                                                    <i className="right fas fa-angle-left"></i>
                                                </p>
                                            </a>
                                            <ul className="nav nav-treeview">
                                                <li className="nav-item">
                                                    <Link to="/rapoarte/companii" className="nav-link">
                                                        <i className="far fa-building nav-icon"></i>
                                                        <p>Companii</p>
                                                    </Link>
                                                </li>
                                                <li className="nav-item">
                                                    <Link to="/rapoarte/moderare" className="nav-link">
                                                        <i className="far fa-images nav-icon"></i>
                                                        <p>Moderare imagini</p>
                                                    </Link>
                                                </li>
                                            </ul>
                                        </li>
                                    )}
                                </>
                            ) : (
                                <li className="nav-item">
                                    <Link to="/login" className="nav-link">
                                        <i className="nav-icon fas fa-sign-in-alt"></i>
                                        <p>Login</p>
                                    </Link>
                                </li>
                            )}
                        </ul>
                    </nav>
                    {/* /.sidebar-menu */}
                </div>
                {/* /.sidebar */}
            </aside>

        </div>
    )
}


export default Sidebar;
